import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Folio, Engraving, Glossarium, Marginalia } from "@/components/sn/SNShell";

export default function StaffNotation1() {
  const { lesson, course, accent } = findLesson("staff-notation", "1");
  return (
    <LessonShell
      course={course}
      courseSlug="staff-notation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/staff-notation/SN1-Three-Dimensions-Animated.mp4"
        caption="The 3 dimensions of staff notation — Pitch · Duration · Time"
        hint="≈ 38 sec · watch first"
      />
      <Folio number="I" title="The first DAW was a piece of paper">
        <p>
          How did staff notation start? Jason puts it this way: imagine a composer in the 17th or
          18th century with a really busy schedule, making music constantly — all these ideas in
          their head — and they had to come up with new pieces very often. In order to bring those
          ideas out and <em>not forget them</em>, they needed some kind of recording medium.
        </p>
        <p>
          So you could argue that staff notation was probably the <strong>first ever DAW</strong> —
          the first ever digital audio workstation. Except it was paper and ink. You pen down all
          your ideas, and then what can you do the next morning? Number one: remember those ideas.
          Number two: be able to communicate those ideas to fellow musicians.
        </p>
        <p>
          That's the double purpose of notation. It started as a memory tool and became a universal
          language — from someone in India to someone in Italy or Spain, people who share no common
          spoken language can communicate music precisely because they share a written one.
        </p>
      </Folio>

      <Folio number="II" title="Three approaches — and most people only know one">
        <p>
          There are three different ways to use staff notation, and they are three separate skills.
          Most music students only ever practice the first.
        </p>
        <Glossarium>
          <p>
            <strong>Read &amp; play</strong> — you read the sheet and you play it. This is what
            most of us do. It is the most popular form. But Jason is clear: don't think of this
            as the <em>only</em> form.
          </p>
          <p>
            <strong>Read &amp; sing</strong> — you read the notation and you sing the notes out
            loud. "It's a very cool way to use staff notation," Jason says. It proves your ear
            can identify pitch without an instrument. Solfège lives here. This bridges notation
            and the inner ear.
          </p>
          <p>
            <strong>Listen &amp; write</strong> — transcription. You hear music and you write
            down what you heard. The ear is the input; the paper is the output. Jason calls
            transcription "the idea of hearing something and knowing exactly what happened." This
            is the hardest skill and also, for a musician, the most powerful.
          </p>
        </Glossarium>
        <p>
          Practice all three. Read and play strengthens performance. Read and sing strengthens
          the ear. Listen and write strengthens transcription and analysis. Together, they make
          you a complete reader of music — not just a decoder of notes.
        </p>
      </Folio>

      <Engraving
        src="/notes/staff-notation/finals/01-evolution-and-3-dimensions-page1.png"
        caption="Jason's whiteboard notes: read→play · read→sing · listen→write — and the three axes of notation."
      />

      <Folio number="III" title="Music has three dimensions — and notation captures all three">
        <p>
          Jason opens this with a physics observation. If you hear any sound that vibrates in a
          way a human being can perceive, there are exactly three things you can say about it.
          It can be <strong>loud or soft</strong>. It can be <strong>high or low</strong>. It
          can be <strong>long or short</strong>. That's it. Volume, pitch, and duration.
        </p>
        <p>
          Staff notation captures all three. The x-axis documents <strong>time</strong>. The
          y-axis documents <strong>pitch</strong>. The z-axis documents <strong>volume</strong>.
          Every symbol in the notation system is saying something about one of those three axes.
          Once you understand that, reading notation stops being a memory exercise and starts
          being a logic exercise.
        </p>
        <Marginalia>
          Timbre — the difference between a violin and a flute playing the same note — is a
          fourth property of sound. But it only tells you which voice or instrument is
          sounding, so Jason sets it aside for now. The three axes cover what notation is actually
          designed to capture.
        </Marginalia>
      </Folio>

      <Folio number="IV" title="Macro and micro — the big picture and the small detail, on the same page">
        <p>
          Here's what makes notation elegant: each axis has both a <em>macro</em> reading
          (the whole-song level) and a <em>micro</em> reading (the single-note level). The same
          page of sheet music communicates both simultaneously.
        </p>
        <Glossarium>
          <p>
            <strong>Time — macro</strong> is the tempo and the time signature. Before the
            metronome, composers used Italian terms: <em>Allegro</em> (lively), <em>Lento</em>{" "}
            (slow), <em>Presto</em> (very fast), <em>Vivace</em> (brisk), <em>Andante</em>{" "}
            (walking pace). These are macro terms — they tell you the rate of flow of the pulse,
            the speed at which your head moves to the song. Now we use exact numbers: 120 beats
            per minute, 74 beats per minute. The time signature (4/4, 3/4, 7/8) tells you how
            many beats fit in each bar.
          </p>
          <p>
            <strong>Time — micro</strong> is the note duration. How long is this particular
            note? How long is this rest? Is it 4 counts? 1 count? Half a count? That is the
            micro level of time.
          </p>
          <p>
            <strong>Pitch — macro</strong> is the key signature — which 7 of the 12 available
            notes are in play for this piece. Jason puts it directly: "A scale has 7 notes,
            right? But because a scale has 7 notes, it does not have 5 — 5 notes are banned."
            The key signature announces which 7 are allowed, just like a raga. It holds for
            the whole composition at the macro level.
          </p>
          <p>
            <strong>Pitch — micro</strong> is the actual note name — is this B♭? G♯? A? —
            and which clef it is written in.
          </p>
          <p>
            <strong>Volume — macro</strong> is the dynamic marking: <em>pp</em> (very soft),{" "}
            <em>p</em> (soft), <em>mf</em> (medium loud), <em>f</em> (loud), <em>ff</em>{" "}
            (very loud). Written at the beginning of a section, this holds until the next
            marking.
          </p>
          <p>
            <strong>Volume — micro</strong> is the accent on a single note — a stress mark, a
            sforzando, or an instrument-specific accentuation like the bow scoop a violinist
            uses on an opening note.
          </p>
        </Glossarium>
      </Folio>

      <Folio number="V" title="Why this matters before we draw a single symbol">
        <p>
          Jason insists on the three-dimensional framework before any clef is drawn because it
          answers the question every student eventually asks: <em>why is this so complicated?</em>{" "}
          The answer is: it isn't. The notation system is complicated in the same way a map is
          complicated — because it has to capture three independent pieces of information at
          once. When you know what those three things are, every symbol you encounter from here
          on has a home.
        </p>
        <p>
          Every lesson that follows is just a more detailed answer to: how do we represent time
          exactly? How do we name a pitch exactly? How do we indicate volume? That's all this
          treatise is.
        </p>
        <Marginalia>
          "You could argue that the staff notation system would have probably been the first
          ever DAW, the first ever digital audio workstation or like recording software. So you
          have all your ideas penned down, you have all your ideas written down." — Jason, on why
          composers invented the system in the first place.
        </Marginalia>
      </Folio>
    </LessonShell>
  );
}
